import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Rss, Bell, User, MessageSquare, Megaphone, RefreshCw, Search, Loader2, Sparkles, Clock, Users, BarChart3, Bookmark, Tag, Plus, X, Globe, UserCircle, Building2, Paperclip, Upload, Trash2 } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { AuroraGlassPanel } from '@easyflow/ui-frontend/components/ui/aurora-glass-panel';
import { LayoutList, AlignJustify } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { AppPageHeader } from '@/components/common/AppPageHeader';
import { AISuggestionStrip } from '@/components/aurora';
import { EmptyState } from '@/components/aurora/EmptyState';
import { apiClient } from '@/lib/api';
import PostComposer from '@/components/feed/PostComposer';
import { MemoPostCard as PostCard } from '@/components/feed/PostCard';
import FeedSidebar from '@/components/feed/FeedSidebar';
import { useDensity } from '@/components/aurora/useDensity';
import { useRegisterShortcuts } from '@/components/aurora/ShortcutsHelp';
import type { FeedPost, FeedComment, FeedReaction, ContentJson } from '@/types/feed';
import type {
  FeedReactionKind,
  FeedRsvpStatus,
  FeedAuthorAnalytics,
  FeedTopicListItem,
  FeedTopicSuggestion,
  FeedEditorListItem,
  DirectoryUserLookup,
  WorkgroupLookup,
  DepartmentLookup,
} from '@easyflow/shared';

interface FeedSocketReactionEvent { postId: string; emoji: string }
interface FeedSocketCommentEvent { postId: string; comment: FeedComment }

const FILTER_TABS = [
  { key: 'all', label: 'Tutti', icon: Rss, color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30' },
  { key: 'following', label: 'Seguiti', icon: Bell, color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30' },
  { key: 'announcements', label: 'Annunci', icon: Megaphone, color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30' },
  { key: 'mine', label: 'I miei post', icon: User, color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/30' },
  { key: 'bookmarks', label: 'Salvati', icon: Bookmark, color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30' },
];

interface ApiResp<T> { success: boolean; data?: T; nextCursor?: string | null }

export default function FeedPageDesktop() {
  const { user } = useAuth();
  const { on, off } = useSocket();
  const { toast } = useToast();
  // Task #1665 — density toggle condiviso (chiave `aurora_ui_density`), coerente
  // con MessengerChat. Compact riduce spacing della lista post.
  const { density, toggle: toggleDensity } = useDensity();
  // Task #1665 — registra le scorciatoie Feed nel modal "?" globale.
  useRegisterShortcuts('feed', [
    { keys: 'D', description: 'Cambia densità (compatta/comoda)', group: 'Feed' },
    { keys: '/', description: 'Focus barra di ricerca',                group: 'Feed' },
    { keys: 'R', description: 'Ricarica il feed',                       group: 'Feed' },
    { keys: '?', description: 'Mostra/chiudi questa finestra',         group: 'Feed' },
  ]);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newPostsBanner, setNewPostsBanner] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [commentsMap, setCommentsMap] = useState<Record<string, FeedComment[]>>({});
  const [editingPost, setEditingPost] = useState<FeedPost | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  // Task #1686 — topic editing + suggerimenti anche in modifica post
  const [editTopicIds, setEditTopicIds] = useState<string[]>([]);
  const [editSuggestedTopics, setEditSuggestedTopics] = useState<FeedTopicSuggestion[]>([]);
  const [editIsSuggesting, setEditIsSuggesting] = useState(false);
  const editLastSuggestedIdsRef = useRef<string[]>([]);
  const editSuggestTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const editSuggestAbortRef = useRef<AbortController | null>(null);
  // Task #1688 — destinatari + allegati anche in modifica.
  interface EditRecipient { id: string; type: 'user' | 'workgroup' | 'department'; name: string }
  const [editRecipients, setEditRecipients] = useState<EditRecipient[]>([]);
  const [editRecipientsDirty, setEditRecipientsDirty] = useState(false);
  const [editRecipientSearch, setEditRecipientSearch] = useState('');
  const [editRecipientResults, setEditRecipientResults] = useState<EditRecipient[]>([]);
  const [editRecipientDropdown, setEditRecipientDropdown] = useState(false);
  const editRecipientTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [editExistingAttachments, setEditExistingAttachments] = useState<Array<{ id: string; filename: string; file_size?: number; mime_type?: string }>>([]);
  const [editRemovedAttachmentIds, setEditRemovedAttachmentIds] = useState<string[]>([]);
  const [editNewFiles, setEditNewFiles] = useState<File[]>([]);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef<NodeJS.Timeout>();
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [order, setOrder] = useState<'for_you' | 'chrono'>('for_you');
  const [topicId, setTopicId] = useState<string>('');
  const [team, setTeam] = useState<string>('');
  const [topics, setTopics] = useState<FeedTopicListItem[]>([]);
  const [teams, setTeams] = useState<DepartmentLookup[]>([]);
  const [isEditor, setIsEditor] = useState(false);

  const [reportingPost, setReportingPost] = useState<{ id: string; title?: string } | null>(null);
  const [reportReason, setReportReason] = useState<'spam' | 'harassment' | 'inappropriate' | 'off_topic' | 'misinformation' | 'other'>('spam');
  const [reportDetail, setReportDetail] = useState('');

  // Task #1662 — Feed v2 state
  const [rsvpMap, setRsvpMap] = useState<Record<string, { yes: number; maybe: number; no: number; my_status: FeedRsvpStatus | null }>>({});
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analytics, setAnalytics] = useState<FeedAuthorAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search]);

  useEffect(() => {
    apiClient.get<Record<string, number>>('/messenger/feed/posts/counts')
      .then(d => { if (d?.success && d.data) setTabCounts(d.data); })
      .catch(() => {});
  }, [posts.length]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isModerator = isAdmin || isEditor;

  const userId = user?.id;

  const loadTopics = useCallback(async () => {
    if (!userId) return;
    try {
      const d = await apiClient.get<FeedTopicListItem[]>('/messenger/feed/topics');
      if (d.success) setTopics(d.data || []);
    } catch { /* silent */ }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    loadTopics();
    apiClient.get<DepartmentLookup[]>('/departments')
      .then(d => { if (d.success) setTeams(d.data || []); })
      .catch(() => {});
    if (!isAdmin) {
      apiClient.get<FeedEditorListItem[]>('/messenger/feed/editors')
        .then(d => {
          if (d.success && Array.isArray(d.data)) {
            setIsEditor(d.data.some((e) => e.user_id === userId));
          }
        })
        .catch(() => {});
    } else {
      setIsEditor(true);
    }
  }, [loadTopics, isAdmin, userId]);

  const handleToggleFollowTopic = async (id: string, follow: boolean) => {
    if (!userId) return;
    try {
      if (follow) await apiClient.post(`/messenger/feed/topics/${id}/follow`, {});
      else await apiClient.delete(`/messenger/feed/topics/${id}/follow`);
    } catch { /* lo stato locale resta invariato se la chiamata fallisce */ return; }
    setTopics(prev => prev.map(t => t.id === id ? { ...t, is_following: follow } : t));
  };

  const handleReportSubmit = async () => {
    if (!reportingPost || !userId) return;
    try {
      await apiClient.post(`/messenger/feed/posts/${reportingPost.id}/report`, { reason: reportReason, detail: reportDetail || undefined });
      toast({ title: 'Segnalazione inviata', description: 'Il team modera la richiesta a breve.' });
    } catch {
      toast({ title: 'Errore', description: 'Impossibile inviare la segnalazione', variant: 'destructive' });
    }
    setReportingPost(null);
    setReportReason('spam');
    setReportDetail('');
  };

  const fetchPosts = useCallback(async (cursor?: string) => {
    const params = new URLSearchParams();
    if (filter && filter !== 'all') params.set('filter', filter);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (cursor) params.set('cursor', cursor);
    if (order) params.set('order', order);
    if (topicId) params.set('topic_id', topicId);
    if (team) params.set('team', team);
    params.set('limit', '20');

    try {
      const data = await apiClient.get(`/messenger/feed/posts?${params}`);
      if (data?.success) {
        const list = (data.data as FeedPost[] | undefined) ?? [];
        if (cursor) setPosts(prev => [...prev, ...list]);
        else setPosts(list);
        setNextCursor((data as { nextCursor?: string | null }).nextCursor || null);
        // Preload RSVP per event posts visibili
        list.filter((p) => p.post_type === 'event').forEach((p) => {
          apiClient.get<typeof rsvpMap[string]>(`/messenger/feed/posts/${p.id}/rsvp`)
            .then(r => { if (r?.success && r.data) setRsvpMap(prev => ({ ...prev, [p.id]: r.data! })); })
            .catch(() => {});
        });
      }
    } catch (err) {
      console.error('Failed to fetch feed posts:', err);
      toast({ title: 'Errore', description: 'Impossibile caricare i post del feed', variant: 'destructive' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter, debouncedSearch, order, topicId, team, toast]);

  useEffect(() => {
    setLoading(true);
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    const handleNewPost = (data: FeedPost) => {
      if (scrollRef.current && scrollRef.current.scrollTop > 200) {
        setNewPostsBanner(prev => prev + 1);
      } else {
        setPosts(prev => [data, ...prev]);
      }
    };

    const handleNewComment = (data: FeedSocketCommentEvent) => {
      if (data.postId && data.comment) {
        setCommentsMap(prev => ({ ...prev, [data.postId]: [...(prev[data.postId] || []), data.comment] }));
        setPosts(prev => prev.map(p => p.id === data.postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
      }
    };
    const handleReaction = (data: FeedSocketReactionEvent & { reaction_kind?: FeedReactionKind }) => {
      if (data.postId && data.emoji) {
        setPosts(prev => prev.map(p => {
          if (p.id !== data.postId) return p;
          const reactions: FeedReaction[] = [...(p.reactions || [])];
          const existing = reactions.find((r) => r.emoji === data.emoji || (data.reaction_kind ? r.reaction_kind === data.reaction_kind : false));
          if (existing) {
            existing.count = (existing.count || 0) + 1;
          } else {
            reactions.push({ emoji: data.emoji, reaction_kind: data.reaction_kind, count: 1 });
          }
          return { ...p, reactions };
        }));
      }
    };
    on('feed:newPost', handleNewPost);
    on('feed:newComment', handleNewComment);
    on('feed:reaction', handleReaction);
    return () => {
      off('feed:newPost', handleNewPost);
      off('feed:newComment', handleNewComment);
      off('feed:reaction', handleReaction);
    };
  }, [on, off, fetchPosts]);

  const handleCreatePost = async (data: Record<string, unknown>) => {
    if (data._uploaded && data._uploadedPost) {
      setPosts(prev => [data._uploadedPost as FeedPost, ...prev]);
      toast({ title: 'Pubblicato', description: 'File pubblicato con successo' });
      fetchPosts();
      return;
    }
    try {
      const result = await apiClient.post('/messenger/feed/posts', data);
      if (result?.success && result.data) {
        setPosts(prev => [result.data as FeedPost, ...prev]);
        toast({ title: 'Pubblicato', description: 'Post pubblicato con successo' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Errore nella pubblicazione del post', variant: 'destructive' });
      throw new Error('Errore nella pubblicazione');
    }
  };

  const handleReact = async (postId: string, emoji: string, kind?: FeedReactionKind) => {
    const k = kind ?? 'like';
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const reactions: FeedReaction[] = [...(p.reactions || [])];
      const existing = reactions.find((r) => r.reaction_kind === k || r.emoji === emoji);
      if (existing) {
        existing.count = (existing.count || 0) + 1;
      } else {
        reactions.push({ emoji, reaction_kind: k, count: 1 });
      }
      return { ...p, reactions };
    }));
    try {
      await apiClient.post(`/messenger/feed/posts/${postId}/reactions-v2`, { kind: k, emoji });
    } catch {
      // Fallback rotta legacy
      await apiClient.post(`/messenger/feed/posts/${postId}/reactions`, { emoji }).catch(() => {});
    }
  };

  const handleComment = async (postId: string, html: string, json: ContentJson) => {
    try {
      const data = await apiClient.post<FeedComment>(`/messenger/feed/posts/${postId}/comments`, { content_html: html, content_json: json });
      if (data?.success && data.data) {
        setCommentsMap(prev => ({
          ...prev,
          [postId]: [...(prev[postId] || []), data.data!],
        }));
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p
        ));
      }
    } catch {
      toast({ title: 'Errore', description: 'Commento non inviato', variant: 'destructive' });
    }
  };

  const handleDelete = async (postId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo post?')) return;
    await apiClient.delete(`/messenger/feed/posts/${postId}`).catch(() => {});
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  const handlePin = async (postId: string, pin: boolean) => {
    if (pin) await apiClient.post(`/messenger/feed/posts/${postId}/pin`, {}).catch(() => {});
    else await apiClient.delete(`/messenger/feed/posts/${postId}/pin`).catch(() => {});
    fetchPosts();
  };

  const handleFollow = async (postId: string, follow: boolean) => {
    if (follow) await apiClient.post(`/messenger/feed/posts/${postId}/follow`, {}).catch(() => {});
    else await apiClient.delete(`/messenger/feed/posts/${postId}/follow`).catch(() => {});
  };

  const handleMarkRead = async (postId: string) => {
    await apiClient.post(`/messenger/feed/posts/${postId}/mark-read`, {}).catch(() => {});
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_read: true, read_count: (p.read_count || 0) + 1 } : p));
  };

  const handleVotePoll = async (pollId: string, optionId: string) => {
    try {
      await apiClient.post(`/messenger/feed/polls/${pollId}/vote`, { option_id: optionId });
      fetchPosts();
    } catch { /* ignore */ }
  };

  const handleLoadComments = async (postId: string) => {
    try {
      const data = await apiClient.get(`/messenger/feed/posts/${postId}/comments`);
      if (data?.success) setCommentsMap(prev => ({ ...prev, [postId]: (data.data as FeedComment[] | undefined) ?? [] }));
    } catch { /* ignore */ }
  };

  const handleCreateTask = async (postId: string) => {
    try {
      const data = await apiClient.post<{ prefill: unknown }>(`/messenger/feed/posts/${postId}/create-task`, {});
      if (data?.success && data.data) {
        window.open(`/${user?.tenantSlug}/tasks?prefill=${encodeURIComponent(JSON.stringify(data.data.prefill))}`, '_blank');
      }
    } catch { /* ignore */ }
  };

  const handleCreateAppointment = async (postId: string) => {
    try {
      const data = await apiClient.post<{ prefill: unknown }>(`/messenger/feed/posts/${postId}/create-appointment`, {});
      if (data?.success && data.data) {
        window.open(`/${user?.tenantSlug}/calendar?prefill=${encodeURIComponent(JSON.stringify(data.data.prefill))}`, '_blank');
      }
    } catch { /* ignore */ }
  };

  const handleEdit = (post: FeedPost) => {
    setEditingPost(post);
    setEditTitle(post.title || '');
    setEditContent(post.content_html || '');
    // Task #1686 — preload topic IDs già associati al post
    setEditTopicIds((post.topics || []).map(t => t.id));
    setEditSuggestedTopics([]);
    editLastSuggestedIdsRef.current = [];
    // Task #1688 — preload destinatari e allegati esistenti
    setEditRecipients((post.recipients || []).map(r => ({
      id: r.recipient_id,
      type: r.recipient_type,
      name: r.display_name || r.recipient_id,
    })));
    setEditRecipientsDirty(false);
    setEditRecipientSearch('');
    setEditRecipientResults([]);
    setEditRecipientDropdown(false);
    setEditExistingAttachments((post.attachments || []).map(a => ({
      id: a.id, filename: a.filename, file_size: a.file_size, mime_type: a.mime_type,
    })));
    setEditRemovedAttachmentIds([]);
    setEditNewFiles([]);
  };

  // Task #1688 — search destinatari (utenti, gruppi, reparti) per editor di modifica.
  useEffect(() => {
    if (editRecipientTimerRef.current) clearTimeout(editRecipientTimerRef.current);
    if (!editingPost) { setEditRecipientResults([]); setEditRecipientDropdown(false); return; }
    if (!editRecipientSearch.trim() || editRecipientSearch.length < 2) {
      setEditRecipientResults([]); setEditRecipientDropdown(false); return;
    }
    editRecipientTimerRef.current = setTimeout(async () => {
      if (!userId) return;
      try {
        const q = encodeURIComponent(editRecipientSearch);
        const [usersRes, groupsRes, deptsRes] = await Promise.allSettled([
          apiClient.get<DirectoryUserLookup[]>(`/users/search?search=${q}&limit=5`),
          apiClient.get<WorkgroupLookup[]>(`/workgroups?search=${q}&limit=5`),
          apiClient.get<DepartmentLookup[]>(`/departments?search=${q}&limit=5`),
        ]);
        const all: EditRecipient[] = [];
        if (usersRes.status === 'fulfilled' && usersRes.value.success) {
          for (const u of (usersRes.value.data || [])) {
            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id;
            all.push({ id: u.id, type: 'user', name });
          }
        }
        if (groupsRes.status === 'fulfilled' && groupsRes.value.success) {
          for (const g of (groupsRes.value.data || [])) {
            all.push({ id: g.id, type: 'workgroup', name: g.name || g.id });
          }
        }
        if (deptsRes.status === 'fulfilled' && deptsRes.value.success) {
          for (const d of (deptsRes.value.data || [])) {
            all.push({ id: d.id, type: 'department', name: d.name || d.id });
          }
        }
        const hasGroupOrDept = editRecipients.some(r => r.type === 'workgroup' || r.type === 'department');
        const filtered = all.filter(r => !editRecipients.some(s => s.id === r.id && s.type === r.type)
          && !(hasGroupOrDept && (r.type === 'workgroup' || r.type === 'department')));
        setEditRecipientResults(filtered);
        setEditRecipientDropdown(filtered.length > 0);
      } catch { /* silent */ }
    }, 300);
    return () => { if (editRecipientTimerRef.current) clearTimeout(editRecipientTimerRef.current); };
  }, [editRecipientSearch, editRecipients, editingPost, userId]);

  const addEditRecipient = (r: EditRecipient) => {
    // No mix user + group/department, simmetrico in entrambe le direzioni
    // (stesso vincolo del composer): se aggiungo un tipo incompatibile col
    // contenuto attuale, sostituisco l'intera lista col nuovo elemento.
    const hasUser = editRecipients.some(x => x.type === 'user');
    const hasGroupOrDept = editRecipients.some(x => x.type === 'workgroup' || x.type === 'department');
    const isGroupOrDept = r.type === 'workgroup' || r.type === 'department';
    const isUser = r.type === 'user';
    if ((hasUser && isGroupOrDept) || (hasGroupOrDept && isUser)) {
      setEditRecipients([r]);
    } else if (hasGroupOrDept && isGroupOrDept) {
      // gruppi/reparti: sempre uno solo, sostituisce il precedente.
      setEditRecipients([r]);
    } else {
      setEditRecipients(prev => [...prev, r]);
    }
    setEditRecipientsDirty(true);
    setEditRecipientSearch('');
    setEditRecipientResults([]);
    setEditRecipientDropdown(false);
  };

  const removeEditRecipient = (id: string, type: EditRecipient['type']) => {
    setEditRecipients(prev => prev.filter(r => !(r.id === id && r.type === type)));
    setEditRecipientsDirty(true);
  };

  const toggleRemoveExistingAttachment = (id: string) => {
    setEditRemovedAttachmentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleEditFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setEditNewFiles(prev => [...prev, ...Array.from(e.target.files!)]);
  };

  const removeEditNewFile = (idx: number) => {
    setEditNewFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleEditSubmit = async () => {
    if (!editingPost) return;
    const originalTopicIds = (editingPost.topics || []).map(t => t.id);
    const topicsChanged =
      editTopicIds.length !== originalTopicIds.length ||
      editTopicIds.some(id => !originalTopicIds.includes(id));

    // Task #1688 — sempre FormData per supportare upload nuovi allegati.
    const fd = new FormData();
    if (editTitle) fd.append('title', editTitle);
    if (editContent) fd.append('content_html', editContent);
    if (topicsChanged) fd.append('topic_ids', JSON.stringify(editTopicIds));
    if (editRecipientsDirty) {
      const payload = editRecipients.map(r => ({
        recipient_type: r.type,
        recipient_id: r.id,
      }));
      fd.append('recipients', JSON.stringify(payload));
    }
    if (editRemovedAttachmentIds.length > 0) {
      fd.append('remove_attachment_ids', JSON.stringify(editRemovedAttachmentIds));
    }
    for (const f of editNewFiles) fd.append('files', f);

    try {
      const data = await apiClient.patch(`/messenger/feed/posts/${editingPost.id}`, fd);
      if (data?.success && data.data) {
        const updated = data.data as Partial<FeedPost>;
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, ...updated, is_edited: true } : p));
        toast({ title: 'Modificato', description: 'Post aggiornato con successo' });

        // Task #1686 — audit feedback suggerimenti topic con post_id valorizzato
        const suggestedIds = editLastSuggestedIdsRef.current;
        if (suggestedIds.length > 0) {
          const accepted = suggestedIds.filter(id => editTopicIds.includes(id));
          void apiClient.post('/messenger/feed/topics/suggest/feedback', {
            suggested_topic_ids: suggestedIds,
            accepted_topic_ids: accepted,
            post_id: editingPost.id,
          }).catch(() => { /* silent */ });
          editLastSuggestedIdsRef.current = [];
        }
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile modificare il post', variant: 'destructive' });
    }
    setEditingPost(null);
  };

  // Task #1686 — debounced fetch suggerimenti topic basato su titolo+contenuto correnti
  useEffect(() => {
    if (editSuggestTimerRef.current) clearTimeout(editSuggestTimerRef.current);
    if (!editingPost) { setEditSuggestedTopics([]); editLastSuggestedIdsRef.current = []; return; }
    if (topics.length === 0) { setEditSuggestedTopics([]); return; }
    const plain = editContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const composite = `${editTitle || ''} ${plain}`.trim();
    if (composite.length < 20) { setEditSuggestedTopics([]); editLastSuggestedIdsRef.current = []; return; }
    editSuggestTimerRef.current = setTimeout(async () => {
      if (!userId) return;
      if (editSuggestAbortRef.current) editSuggestAbortRef.current.abort();
      const ctrl = new AbortController();
      editSuggestAbortRef.current = ctrl;
      setEditIsSuggesting(true);
      try {
        const data = await apiClient.post<FeedTopicSuggestion[]>(
          '/messenger/feed/topics/suggest',
          { title: editTitle, content_html: editContent },
          { signal: ctrl.signal },
        );
        if (data.success && Array.isArray(data.data)) {
          const list = data.data;
          setEditSuggestedTopics(list);
          editLastSuggestedIdsRef.current = list.map(s => s.id);
        }
      } catch { /* aborted/silent */ }
      finally { setEditIsSuggesting(false); }
    }, 600);
    return () => { if (editSuggestTimerRef.current) clearTimeout(editSuggestTimerRef.current); };
  }, [editingPost, editTitle, editContent, topics.length, userId]);

  const toggleEditTopic = (id: string) => {
    setEditTopicIds(prev => {
      if (prev.includes(id)) return prev.filter(t => t !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const acceptEditSuggestion = (id: string) => {
    if (editTopicIds.includes(id) || editTopicIds.length >= 3) return;
    setEditTopicIds(prev => [...prev, id]);
  };

  const dismissEditSuggestion = (id: string) => {
    setEditSuggestedTopics(prev => prev.filter(s => s.id !== id));
  };

  const handleCreateVideocall = async (postId: string) => {
    try {
      // Task #3599 — l'endpoint reale è POST /video/rooms (l'ex /video-calls
      // non è mai stato montato); la room ritorna meeting_link, non join_url.
      const data = await apiClient.post<{ id?: string; meeting_link?: string }>('/video/rooms', {
        title: 'Videochiamata dal Feed',
      });
      if (data?.success && data.data?.meeting_link) window.open(data.data.meeting_link, '_blank');
      else window.open(`/${user?.tenantSlug}/video-calls`, '_blank');
    } catch {
      window.open(`/${user?.tenantSlug}/video-calls`, '_blank');
    }
  };

  // Task #1662 — bookmark
  const handleBookmark = async (postId: string, bookmarked: boolean) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_bookmarked: bookmarked } : p));
    try {
      if (bookmarked) await apiClient.post(`/messenger/feed/posts/${postId}/bookmark`, {});
      else await apiClient.delete(`/messenger/feed/posts/${postId}/bookmark`);
    } catch {
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_bookmarked: !bookmarked } : p));
      toast({ title: 'Errore', description: 'Operazione segnalibro fallita', variant: 'destructive' });
    }
  };

  // Task #1662 — RSVP
  const handleRsvp = async (postId: string, status: FeedRsvpStatus) => {
    try {
      const data = await apiClient.post<typeof rsvpMap[string]>(`/messenger/feed/posts/${postId}/rsvp`, { status });
      if (data?.success && data.data) setRsvpMap(prev => ({ ...prev, [postId]: data.data! }));
    } catch {
      toast({ title: 'Errore', description: 'Impossibile aggiornare RSVP', variant: 'destructive' });
    }
  };

  // Task #1662 — Boost
  const handleBoost = async (postId: string, hours: number) => {
    try {
      const data = await apiClient.post<{ boost_until: string | null }>(`/messenger/feed/posts/${postId}/boost`, { hours });
      if (data?.success && data.data) {
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, boost_until: data.data!.boost_until } : p));
        toast({ title: 'Boost attivato', description: `In evidenza per ${hours}h` });
      }
    } catch {
      toast({ title: 'Errore', description: 'Boost non riuscito', variant: 'destructive' });
    }
  };

  const handleClearBoost = async (postId: string) => {
    try {
      await apiClient.delete(`/messenger/feed/posts/${postId}/boost`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, boost_until: null } : p));
    } catch { /* ignore */ }
  };

  // Task #1662 — Q&A official answer
  const handleMarkOfficial = async (postId: string, commentId: string) => {
    try {
      await apiClient.post(`/messenger/feed/posts/${postId}/official-answer`, { comment_id: commentId });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, official_answer_comment_id: commentId } : p));
      toast({ title: 'Risposta ufficiale', description: 'Commento marcato come risposta ufficiale' });
    } catch {
      toast({ title: 'Errore', description: 'Impossibile marcare la risposta ufficiale', variant: 'destructive' });
    }
  };

  const handleUnmarkOfficial = async (postId: string) => {
    try {
      await apiClient.delete(`/messenger/feed/posts/${postId}/official-answer`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, official_answer_comment_id: null } : p));
    } catch { /* ignore */ }
  };

  // Task #1662 — Author analytics
  const openAnalytics = async () => {
    setAnalyticsOpen(true);
    setAnalyticsLoading(true);
    try {
      const data = await apiClient.get<FeedAuthorAnalytics>('/messenger/feed/me/analytics?range_days=30');
      if (data?.success && data.data) setAnalytics(data.data);
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare le analytics', variant: 'destructive' });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    fetchPosts(nextCursor);
  };

  const showNewPosts = () => {
    setNewPostsBanner(0);
    fetchPosts();
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-950">
      <AuroraGlassPanel as="header" intensity="medium" className="border-b shadow-sm sticky top-0 z-20">
        <div className="px-3 md:px-6 py-4">
          <AppPageHeader
            noBorder
            className="pb-0"
            icon={
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm shrink-0">
                <Rss className="h-5 w-5 text-white" />
              </div>
            }
            title="Feed Aziendale"
            subtitle="Stream di comunicazione aziendale"
            actions={
              <div className="flex items-center gap-2 w-full md:w-auto">
                <Button variant="outline" size="sm" onClick={openAnalytics} className="gap-1.5 rounded-lg" title="Le mie analytics">
                  <BarChart3 className="h-4 w-4" /> Analytics
                </Button>
                <div className="relative flex-1 md:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Cerca nel feed..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-lg"
                  />
                </div>
              </div>
            }
          />

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5">
              <button
                onClick={() => setOrder('for_you')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all",
                  order === 'for_you' ? "bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-sm" : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
                title="Post ordinati per rilevanza personale"
              >
                <Sparkles className="h-3.5 w-3.5" /> Per te
              </button>
              <button
                onClick={() => setOrder('chrono')}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all",
                  order === 'chrono' ? "bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm" : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
                title="Ordine cronologico"
              >
                <Clock className="h-3.5 w-3.5" /> Recenti
              </button>
            </div>
            <button
              type="button"
              onClick={toggleDensity}
              title={density === 'compact' ? 'Densità: compatta (clicca per comoda)' : 'Densità: comoda (clicca per compatta)'}
              aria-label={`Cambia densità (attuale: ${density === 'compact' ? 'compatta' : 'comoda'})`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              {density === 'compact' ? <AlignJustify className="h-3.5 w-3.5" /> : <LayoutList className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{density === 'compact' ? 'Compatta' : 'Comoda'}</span>
            </button>
            {topics.length > 0 && (
              <select
                value={topicId}
                onChange={e => setTopicId(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-700 dark:text-gray-300"
              >
                <option value="">Tutti i topic</option>
                {topics.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
            {teams.length > 0 && (
              <select
                value={team}
                onChange={e => setTeam(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-700 dark:text-gray-300"
              >
                <option value="">Tutti i team</option>
                {teams.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-center gap-1 mt-3 overflow-x-auto">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={cn(
                  "flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap shrink-0",
                  filter === tab.key
                    ? tab.color
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {(tabCounts[tab.key] ?? 0) > 0 && (
                  <span className={cn(
                    "text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center",
                    filter === tab.key
                      ? "bg-white/30 text-current"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  )}>
                    {tabCounts[tab.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </AuroraGlassPanel>

      <div className="flex-1 overflow-y-auto" ref={scrollRef}>
        <div className="px-3 md:px-6 py-4 md:py-6">
          <div className="flex flex-col md:flex-row md:gap-6">
            <div className="flex-1 min-w-0 space-y-4">
              <PostComposer onSubmit={handleCreatePost} isAdmin={isAdmin} />

              <AISuggestionStrip
                title="Riepilogo AI del feed"
                body="Lascia che l'AI riassuma gli aggiornamenti del feed e suggerisca i post più rilevanti per te."
              />

              {newPostsBanner > 0 && (
                <button
                  onClick={showNewPosts}
                  className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md"
                >
                  <RefreshCw className="h-4 w-4" />
                  {newPostsBanner} {newPostsBanner === 1 ? 'nuovo post' : 'nuovi post'}
                </button>
              )}

              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <p className="text-sm text-gray-400">Caricamento feed...</p>
                </div>
              ) : posts.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-xl border shadow-sm">
                  <EmptyState
                    illustration={filter === 'bookmarks' ? 'no-bookmarks' : filter === 'reports' ? 'no-reports' : search ? 'no-search' : 'no-posts'}
                    title={filter === 'bookmarks' ? 'Nessun segnalibro'
                      : filter === 'reports' ? 'Nessuna segnalazione'
                      : search ? 'Nessun risultato'
                      : 'Nessun post nel feed'}
                    subtitle={filter === 'bookmarks' ? 'Salva i post che vuoi rileggere: appariranno qui.'
                      : filter === 'reports' ? 'Nessun contenuto segnalato richiede attenzione.'
                      : search ? 'Prova a modificare i termini di ricerca o rimuovere i filtri.'
                      : 'Inizia condividendo qualcosa con il tuo team — un aggiornamento, una vittoria, una domanda.'}
                  />
                </div>
              ) : (
                <div role="feed" aria-busy={loading} aria-label="Stream Feed aziendale" className={cn('flex flex-col', density === 'compact' ? 'gap-2' : 'gap-4')}>
                  {posts.map((post, i) => (
                    <PostCard
                      key={post.id}
                      index={i}
                      post={post}
                      currentUserId={user?.id || ''}
                      isAdmin={isModerator}
                      onReact={handleReact}
                      onComment={handleComment}
                      onDelete={handleDelete}
                      onEdit={handleEdit}
                      onPin={handlePin}
                      onFollow={handleFollow}
                      onMarkRead={handleMarkRead}
                      onCreateTask={handleCreateTask}
                      onCreateAppointment={handleCreateAppointment}
                      onCreateVideocall={handleCreateVideocall}
                      onVotePoll={handleVotePoll}
                      onReport={(p) => { setReportingPost({ id: p.id, title: p.title ?? undefined }); setReportReason('spam'); setReportDetail(''); }}
                      comments={commentsMap[post.id]}
                      onLoadComments={handleLoadComments}
                      onBookmark={handleBookmark}
                      onRsvp={handleRsvp}
                      onBoost={handleBoost}
                      onClearBoost={handleClearBoost}
                      onMarkOfficial={handleMarkOfficial}
                      onUnmarkOfficial={handleUnmarkOfficial}
                      rsvpSummary={rsvpMap[post.id] ?? null}
                    />
                  ))}

                  {nextCursor && (
                    <div className="flex justify-center py-4">
                      <Button variant="outline" onClick={loadMore} disabled={loadingMore} className="gap-2 rounded-xl">
                        {loadingMore ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</>
                        ) : (
                          'Carica altri post'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="w-80 shrink-0 hidden md:block">
              <div className="sticky top-4">
                <FeedSidebar
                  tenantSlug={user?.tenantSlug || ''}
                  topics={topics}
                  isModerator={isModerator}
                  onToggleFollowTopic={handleToggleFollowTopic}
                  onSelectTopic={setTopicId}
                  selectedTopicId={topicId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!reportingPost} onOpenChange={(open) => { if (!open) setReportingPost(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Segnala post</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {reportingPost?.title && (
              <p className="text-xs text-gray-500 truncate">Post: <strong>{reportingPost.title}</strong></p>
            )}
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1">Motivo</label>
              <select
                value={reportReason}
                onChange={e => setReportReason(e.target.value as typeof reportReason)}
                className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-900 dark:border-gray-700 text-sm"
              >
                <option value="spam">Spam</option>
                <option value="harassment">Molestia / linguaggio offensivo</option>
                <option value="inappropriate">Contenuto inappropriato</option>
                <option value="off_topic">Fuori tema</option>
                <option value="misinformation">Disinformazione</option>
                <option value="other">Altro</option>
              </select>
            </div>
            <Textarea
              placeholder="Dettagli (opzionale)"
              value={reportDetail}
              onChange={e => setReportDetail(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportingPost(null)}>Annulla</Button>
            <Button onClick={handleReportSubmit} className="bg-red-600 hover:bg-red-700">Invia segnalazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPost} onOpenChange={(open) => { if (!open) setEditingPost(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Modifica post</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              placeholder="Titolo (opzionale)"
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              className="font-semibold"
            />
            <Textarea
              placeholder="Contenuto del post..."
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              rows={5}
              className="resize-none"
            />

            {/* Task #1686 — suggerimenti topic anche in modifica */}
            {editSuggestedTopics.filter(s => !editTopicIds.includes(s.id)).length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 mr-1">
                  <Sparkles className="h-3 w-3" /> Suggeriti{editIsSuggesting ? '…' : ':'}
                </span>
                {editSuggestedTopics.filter(s => !editTopicIds.includes(s.id)).map(s => {
                  const disabled = editTopicIds.length >= 3;
                  return (
                    <span key={s.id} className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/20">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => acceptEditSuggestion(s.id)}
                        title={`Aggiungi topic suggerito (${s.reason})`}
                        className={cn(
                          "inline-flex items-center gap-1 pl-2 pr-1.5 py-0.5 rounded-l-full text-[11px] font-medium transition-all",
                          disabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                        )}
                        style={s.color ? { color: s.color } : undefined}
                      >
                        <Plus className="h-3 w-3" />
                        {s.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissEditSuggestion(s.id)}
                        title="Ignora suggerimento"
                        className="px-1 py-0.5 rounded-r-full text-indigo-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Task #1688 — destinatari */}
            <div className="relative">
              <div className="text-[11px] font-medium text-gray-500 mb-1 flex items-center gap-1">
                <Users className="h-3 w-3" /> Destinatari
              </div>
              <div className="flex flex-wrap items-center gap-1.5 p-2 min-h-[40px] border rounded-lg bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring">
                {editRecipients.length === 0 && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-md">
                    <Globe className="h-3 w-3" /> Tutti
                  </span>
                )}
                {editRecipients.map(r => {
                  const Icon = r.type === 'user' ? UserCircle : r.type === 'workgroup' ? Users : Building2;
                  const cls = r.type === 'user'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : r.type === 'workgroup'
                      ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
                  return (
                    <span key={`${r.type}-${r.id}`} className={cn("inline-flex items-center gap-1 text-xs pl-1.5 pr-1 py-0.5 rounded-md", cls)}>
                      <Icon className="h-3 w-3" />
                      {r.name}
                      <button onClick={() => removeEditRecipient(r.id, r.type)} className="hover:text-red-500 ml-0.5">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
                <input
                  type="text"
                  value={editRecipientSearch}
                  onChange={e => setEditRecipientSearch(e.target.value)}
                  placeholder={editRecipients.length === 0 ? 'Cerca utenti, gruppi o reparti…' : 'Aggiungi…'}
                  className="flex-1 min-w-[140px] bg-transparent text-sm outline-none placeholder:text-gray-400"
                  onBlur={() => setTimeout(() => setEditRecipientDropdown(false), 200)}
                  onFocus={() => { if (editRecipientResults.length > 0) setEditRecipientDropdown(true); }}
                />
              </div>
              {editRecipientDropdown && editRecipientResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-900 border rounded-lg shadow-lg max-h-56 overflow-auto">
                  {editRecipientResults.map((r) => {
                    const Icon = r.type === 'user' ? UserCircle : r.type === 'workgroup' ? Users : Building2;
                    return (
                      <button
                        key={`${r.type}-${r.id}`}
                        onMouseDown={() => addEditRecipient(r)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
                      >
                        <Icon className="h-4 w-4 text-gray-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs truncate">{r.name}</p>
                          <p className="text-[10px] text-gray-400">{r.type === 'user' ? 'Utente' : r.type === 'workgroup' ? 'Gruppo' : 'Reparto'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Task #1688 — allegati */}
            <div>
              <div className="text-[11px] font-medium text-gray-500 mb-1 flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Allegati
              </div>
              <div className="space-y-1.5">
                {editExistingAttachments.map(a => {
                  const removed = editRemovedAttachmentIds.includes(a.id);
                  return (
                    <div key={a.id} className={cn("flex items-center gap-2 px-2 py-1.5 rounded-lg border text-xs", removed ? "opacity-50 line-through bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800" : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700")}>
                      <Paperclip className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="flex-1 truncate">{a.filename}</span>
                      <button onClick={() => toggleRemoveExistingAttachment(a.id)} className="text-gray-400 hover:text-red-500" title={removed ? 'Annulla rimozione' : 'Rimuovi'}>
                        {removed ? <RefreshCw className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  );
                })}
                {editNewFiles.map((f, i) => (
                  <div key={`new-${i}`} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 text-xs">
                    <Upload className="h-3 w-3 text-blue-500 shrink-0" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-[10px] text-gray-400">{Math.round(f.size / 1024)} KB</span>
                    <button onClick={() => removeEditNewFile(i)} className="text-gray-400 hover:text-red-500">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => editFileInputRef.current?.click()}
                  className="w-full px-3 py-2 text-xs font-medium text-gray-500 hover:text-blue-600 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg hover:border-blue-400 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" /> Aggiungi allegato
                </button>
                <input
                  ref={editFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleEditFileSelect}
                />
              </div>
            </div>

            {topics.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 mr-1">
                  <Tag className="h-3 w-3" /> Topic ({editTopicIds.length}/3):
                </span>
                {topics.map(t => {
                  const isSel = editTopicIds.includes(t.id);
                  const disabled = !isSel && editTopicIds.length >= 3;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => !disabled && toggleEditTopic(t.id)}
                      disabled={disabled}
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all",
                        isSel
                          ? "border-transparent text-white shadow-sm"
                          : disabled
                            ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600 dark:border-gray-700"
                            : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                      )}
                      style={isSel ? { backgroundColor: t.color || '#6366f1' } : undefined}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPost(null)}>Annulla</Button>
            <Button onClick={handleEditSubmit} className="bg-blue-600 hover:bg-blue-700">Salva modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #1662 — Author analytics dialog */}
      <Dialog open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" /> Le tue analytics (ultimi 30 giorni)
            </DialogTitle>
          </DialogHeader>
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : analytics ? (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {([
                  ['Post', analytics.totals.posts, 'bg-blue-50 text-blue-700'],
                  ['Reazioni', analytics.totals.reactions, 'bg-pink-50 text-pink-700'],
                  ['Commenti', analytics.totals.comments, 'bg-emerald-50 text-emerald-700'],
                  ['Segnalibri', analytics.totals.bookmarks, 'bg-amber-50 text-amber-700'],
                  ['Visualizz.', analytics.totals.views, 'bg-violet-50 text-violet-700'],
                ] as Array<[string, number, string]>).map(([label, val, cls]) => (
                  <div key={label} className={cn("rounded-xl px-3 py-2.5 border", cls)}>
                    <div className="text-[10px] uppercase font-bold opacity-70">{label}</div>
                    <div className="text-xl font-bold">{val}</div>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Top post</h4>
                <div className="space-y-1.5">
                  {analytics.top_posts.length === 0 ? (
                    <p className="text-sm text-gray-400">Nessun post pubblicato nel periodo.</p>
                  ) : analytics.top_posts.map(p => (
                    <div key={p.post_id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border bg-white dark:bg-gray-800">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{p.title || `[${p.post_type}]`}</div>
                        <div className="text-[10px] text-gray-400">{p.post_type}</div>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] shrink-0">
                        <span className="text-pink-600">❤ {p.total_reactions}</span>
                        <span className="text-emerald-600">💬 {p.total_comments}</span>
                        <span className="text-amber-600 inline-flex items-center gap-0.5"><Bookmark className="h-3 w-3" />{p.total_bookmarks}</span>
                        <span className="text-violet-600">👁 {p.total_views}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-6 text-center">Nessun dato disponibile.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
